import React, { useEffect, useState } from 'react'
import { Key, User, Save, Eye, EyeOff, CheckCircle } from 'lucide-react'
import { Button, Card, Input } from '../components/common'
import useSettingsStore from '../stores/settingsStore'
import useMobileLayout from '../hooks/useMobileLayout'
import MobileSettingsPage from '../components/mobile/MobileSettingsPage'

export default function SettingsPage() {
  const isMobile = useMobileLayout()
  const [surveyorName, setSurveyorName] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)
  const [saved, setSaved] = useState({ surveyor: false, apiKey: false })

  const defaultSurveyor = useSettingsStore(state => state.defaultSurveyor)
  const openaiApiKey = useSettingsStore(state => state.openaiApiKey)
  const loading = useSettingsStore(state => state.loading)
  const loadSettings = useSettingsStore(state => state.loadSettings)
  const setDefaultSurveyor = useSettingsStore(state => state.setDefaultSurveyor)
  const setOpenaiApiKey = useSettingsStore(state => state.setOpenaiApiKey)

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  useEffect(() => {
    setSurveyorName(defaultSurveyor)
    setApiKey(openaiApiKey)
  }, [defaultSurveyor, openaiApiKey])

  // Mobile layout
  if (isMobile) {
    return <MobileSettingsPage />
  }

  const handleSaveSurveyor = async () => {
    try {
      await setDefaultSurveyor(surveyorName)
      setSaved(prev => ({ ...prev, surveyor: true }))
      setTimeout(() => setSaved(prev => ({ ...prev, surveyor: false })), 2000)
    } catch (error) {
      console.error('Failed to save surveyor name:', error)
    }
  }

  const handleSaveApiKey = async () => {
    try {
      await setOpenaiApiKey(apiKey)
      setSaved(prev => ({ ...prev, apiKey: true }))
      setTimeout(() => setSaved(prev => ({ ...prev, apiKey: false })), 2000)
    } catch (error) {
      console.error('Failed to save API key:', error)
    }
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-500 mt-1">
          Configure your application preferences
        </p>
      </div>

      {/* Default Surveyor */}
      <Card>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <User className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Default Surveyor</h2>
            <p className="text-sm text-slate-500 mt-1">
              This name will be pre-filled when creating new surveys
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <Input
            label="Surveyor Name"
            value={surveyorName}
            onChange={(e) => setSurveyorName(e.target.value)}
            placeholder="Enter default surveyor name"
          />
          <Button
            variant="primary"
            onClick={handleSaveSurveyor}
            loading={loading}
            icon={saved.surveyor ? 'CheckCircle' : 'Save'}
          >
            {saved.surveyor ? 'Saved!' : 'Save Changes'}
          </Button>
        </div>
      </Card>

      {/* OpenAI API Key */}
      <Card>
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-accent-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Key className="w-6 h-6 text-accent-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">OpenAI API Key</h2>
            <p className="text-sm text-slate-500 mt-1">
              Required for AI-powered photo analysis using ChatGPT Vision
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              API Key
            </label>
            <div className="relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="sk-..."
                className="w-full px-4 py-3 pr-12 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 font-mono text-sm"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showApiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-sm text-slate-500 mt-2">
              Your API key is stored locally and never sent to our servers.
              Get your API key from{' '}
              <a
                href="https://platform.openai.com/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 hover:text-primary-700 underline"
              >
                OpenAI Platform
              </a>
            </p>
          </div>

          {!apiKey && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-amber-800 text-sm">
                <strong>Note:</strong> AI analysis is currently disabled. Add your OpenAI API key to enable the "Analyse Photos" feature.
              </p>
            </div>
          )}

          <Button
            variant="primary"
            onClick={handleSaveApiKey}
            loading={loading}
            icon={saved.apiKey ? 'CheckCircle' : 'Save'}
          >
            {saved.apiKey ? 'Saved!' : 'Save API Key'}
          </Button>
        </div>
      </Card>

      {/* About */}
      <Card>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">About</h2>
        <div className="space-y-2 text-sm text-slate-600">
          <p><strong>Clystcore Surveyors</strong> - Property Survey Application</p>
          <p>Version 1.0.0</p>
          <p className="mt-4">
            A professional iPad-optimized application for capturing and analysing property survey photos with AI-powered insights.
          </p>
        </div>
      </Card>
    </div>
  )
}
